import { useCallback, useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { ProductFeedbackSheet } from "../components/ProductFeedbackSheet";
import type { ProductFeedbackType } from "../data/product-feedback";

/**
 * Top-bar `?` entry for every signed-in Console user. Opens a small feedback menu that
 * launches the same ProductFeedbackSheet Help footers use, without exposing the Help corpus.
 */
export function TopbarFeedbackMenu({
  client,
  accountId,
  accountName,
  roleLabel,
  pageLabel,
}: {
  client: SupabaseClient;
  accountId: string;
  accountName: string;
  roleLabel: string;
  pageLabel: string;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<ProductFeedbackType | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const feedbackTriggerRef = useRef<HTMLButtonElement | null>(null);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const openFeedback = useCallback((type: ProductFeedbackType, trigger: HTMLButtonElement) => {
    feedbackTriggerRef.current = trigger;
    setFeedbackType(type);
    setMenuOpen(false);
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackType(null);
    window.requestAnimationFrame(() => {
      feedbackTriggerRef.current?.focus();
      feedbackTriggerRef.current = null;
    });
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeMenu();
      triggerRef.current?.focus();
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      closeMenu();
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [closeMenu, menuOpen]);

  return (
    <div className="topbar__feedback">
      <button
        ref={triggerRef}
        type="button"
        className="topbar__help"
        aria-label="Help and feedback"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        ?
      </button>
      {menuOpen ? (
        <div
          ref={menuRef}
          className="topbar-help-menu"
          role="menu"
          aria-label="Help and feedback"
        >
          <div className="topbar-help-menu__label">Feedback</div>
          <button
            type="button"
            role="menuitem"
            className="topbar-help-menu__item"
            onClick={(event) => openFeedback("problem", event.currentTarget)}
          >
            Report a problem
          </button>
          <button
            type="button"
            role="menuitem"
            className="topbar-help-menu__item"
            onClick={(event) => openFeedback("idea", event.currentTarget)}
          >
            Share an idea
          </button>
          <p className="topbar-help-menu__foot">
            A person on the SecOps team reads every report.
          </p>
        </div>
      ) : null}
      {feedbackType !== null ? (
        <ProductFeedbackSheet
          client={client}
          accountId={accountId}
          accountName={accountName}
          roleLabel={roleLabel}
          pageLabel={pageLabel}
          initialType={feedbackType}
          onClose={closeFeedback}
        />
      ) : null}
    </div>
  );
}
